import * as React from "react";
import {
  Card,
  Button,
  HTMLTable,
  Tag,
  Switch,
  Popover,
  Menu,
  MenuItem,
  NonIdealState,
} from "@blueprintjs/core";
import { css } from "@emotion/core";
import { IOrgChartPropertyAssignment } from "../../../api/admin";
import { stickyTableHeaderCss } from "../common/adminCss";

function isAssignmentDeactivated(row: IOrgChartPropertyAssignment): boolean {
  return row.deleted_at != null;
}

export function ChartPropertiesPanel(props: {
  assignments: IOrgChartPropertyAssignment[];
  handleAssign: () => void;
  handleRemove: (assignmentId: number) => void;
  handleActivate: (assignmentId: number) => void;
  showDeactivated: boolean;
  onShowDeactivatedChange: (checked: boolean) => void;
}) {
  const {
    assignments,
    handleAssign,
    handleRemove,
    handleActivate,
    showDeactivated,
    onShowDeactivatedChange,
  } = props;

  const visible = React.useMemo(() => {
    return assignments
      .filter((a) => {
        if (!showDeactivated && isAssignmentDeactivated(a)) return false;
        return true;
      })
      .sort((a, b) => {
        const layerCmp = a.layer_name.localeCompare(b.layer_name);
        if (layerCmp !== 0) return layerCmp;
        const aA = !isAssignmentDeactivated(a);
        const bA = !isAssignmentDeactivated(b);
        if (aA !== bA) return aA ? -1 : 1;
        return a.label.localeCompare(b.label);
      });
  }, [assignments, showDeactivated]);

  return (
    <Card
      css={css`
        padding: 0;
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 11px;
          background-color: var(--neutral-7);
          box-shadow: inset 0 0 1px 1px var(--neutral-5);
        `}
      >
        <h3>Chart Properties</h3>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 1rem;
          `}
        >
          <Switch
            css={css`
              margin: 0;
            `}
            label="Show deactivated"
            checked={showDeactivated}
            onChange={(e) => onShowDeactivatedChange(e.currentTarget.checked)}
          />
          <Button
            icon="plus"
            text="Add"
            intent="primary"
            onClick={handleAssign}
          />
        </div>
      </div>
      {visible.length === 0 ? (
        <NonIdealState
          icon="chart"
          title="No Chart Property Assignments"
          description="Assign global chart properties to layer names for this organization."
        />
      ) : (
        <div
          css={css`
            flex: 1;
            overflow-y: auto;
          `}
        >
          <HTMLTable
            striped
            interactive
            css={[stickyTableHeaderCss, css`
              width: 100%;
            `]}
          >
            <thead>
              <tr>
                <th>Layer name</th>
                <th>Property label</th>
                <th>Status</th>
                <th
                  css={css`
                    width: 64px;
                  `}
                />
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => {
                const inactive = isAssignmentDeactivated(row);
                return (
                  <tr key={row.assignment_id}>
                    <td>
                      <div
                        css={css`
                          color: ${inactive
                            ? "var(--neutral-3)"
                            : "inherit"};
                        `}
                      >
                        {row.layer_name}
                      </div>
                    </td>
                    <td>
                      <div
                        css={css`
                          color: ${inactive
                            ? "var(--neutral-3)"
                            : "inherit"};
                        `}
                      >
                        {row.label}
                      </div>
                    </td>
                    <td>
                      <Tag
                        intent={inactive ? "warning" : "success"}
                        minimal
                      >
                        {inactive ? "Deactivated" : "Active"}
                      </Tag>
                    </td>
                    <td>
                      <Popover
                        content={
                          <Menu>
                            {inactive ? (
                              <MenuItem
                                icon="tick"
                                text="Reactivate"
                                intent="success"
                                onClick={() =>
                                  handleActivate(row.assignment_id)
                                }
                              />
                            ) : (
                              <MenuItem
                                icon="disable"
                                text="Deactivate"
                                intent="danger"
                                onClick={() => handleRemove(row.assignment_id)}
                              />
                            )}
                          </Menu>
                        }
                        placement="bottom-end"
                      >
                        <Button size="small" icon="more" />
                      </Popover>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </HTMLTable>
        </div>
      )}
    </Card>
  );
}
